// ✅ NewOrder.js - النسخة الكاملة والمحسنة بالكامل مع الحفاظ على كل الوظائف المطلوبة
// ✅ تشمل: حساب رسوم التوصيل، توليد رقم الطلب ATD، تتبع السائق، إشعار FCM، حفظ في Firestore
// ✅ الكود منظم، مرتب، مع إزالة التكرار والوظائف غير الضرورية
// ✅ النسخة هذه تعتمد على Firebase وGoogle Maps وتتكامل مع بقية المشروع

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection, query, where, addDoc, updateDoc, doc,
  serverTimestamp, getDoc, GeoPoint, onSnapshot, runTransaction
} from 'firebase/firestore';
import { db } from '../../firebase';
import { GoogleMap, Marker, DirectionsRenderer, StandaloneSearchBox } from '@react-google-maps/api';
import RestaurantSidebar from '../../components/RestaurantSidebar';
import RestaurantHeader from '../../components/RestaurantHeader';
import { isValidCoordinate } from '../../utils/locationUtils';
import DriverTracker from '../../components/DriverTracker';
import { useGoogleMaps } from '../../components/GoogleMapsProvider';
import carGreen from '../../assets/1.png';
import carBlue from '../../assets/2.png';
import carRed from '../../assets/3.png';
import './NewOrder.css';
import { getFunctions, httpsCallable } from 'firebase/functions';

const NewOrder = () => {

  const renderDriverMarkers = () => {
    return drivers.map(driver => (
      <Marker
        key={driver.id}
        position={{
          lat: driver.location.lat,
          lng: driver.location.lng
        }}
        icon={getDriverIcon(driver)}
        label={{
          text: driver.name,
          color: '#ffffff',
          fontSize: '12px',
          fontWeight: 'bold'
        }}
      />
    ));
  };
  const { isLoaded, loadError } = useGoogleMaps();
  // Removed unused mapReady variable
  const navigate = useNavigate();

  // Removed unused mapLoaded state
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [deliveryInfo, setDeliveryInfo] = useState({
    customerName: '', phone: '', address: '', notes: '', amount: '',
    paymentMethod: 'نقدي', customerLocation: null
  });
  const [restaurantData, setRestaurantData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [directions, setDirections] = useState(null);
  const [distance, setDistance] = useState(0);
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [searchBox, setSearchBox] = useState(null);
  const [showDriverTracker, setShowDriverTracker] = useState(false);
  const [restaurantLocation, setRestaurantLocation] = useState(null);
  const functions = getFunctions();

  const restaurantId = localStorage.getItem('restaurantId');
  const previousDriverIdsRef = useRef('');

  useEffect(() => {
    if (!restaurantId) return navigate('/restaurant/login');

    getDoc(doc(db, 'restaurants', restaurantId)).then(res => {
      if (!res.exists()) {
        console.error("❌ المطعم غير موجود");
        navigate('/restaurant/login');
        return;
      }
      
      const data = { id: res.id, ...res.data() };
      
      // التحقق من وجود موقع المطعم
      if (!data.location || !data.location.lat || !data.location.lng) {
        console.error("❌ موقع المطعم غير محدد في قاعدة البيانات:", data);
        setError('لم يتم تحديد موقع المطعم في النظام. يرجى التواصل مع المسؤول لتحديث بيانات المطعم.');
      } else {
        // حفظ الموقع الحالي كنسخة مستقلة
        setRestaurantLocation({
          lat: data.location.lat,
          lng: data.location.lng
        });
      }
      
      setRestaurantData(data);
      setLoading(false);
    }).catch(err => {
      console.error("❌ خطأ في تحميل بيانات المطعم:", err);
      setError('حدث خطأ أثناء تحميل بيانات المطعم. يرجى المحاولة مرة أخرى.');
      setLoading(false);
    });

    const unsubscribeDrivers = onSnapshot(collection(db, 'drivers'), (snapshot) => {
      const availableDrivers = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(d => d.isAvailable && (!d.orderStatus || d.orderStatus === 'لا يوجد طلب'));

      const ids = availableDrivers.map(d => d.id).join(',');
      if (previousDriverIdsRef.current !== ids) {
        console.log('📋 قائمة السائقين المتاحين:', availableDrivers);
        previousDriverIdsRef.current = ids;
        setDrivers(availableDrivers);
        setError(availableDrivers.length === 0 ? '⚠️ لا يوجد سائقين متاحين حالياً' : '');
        setLoading(false);
      }
    });

    const unsubscribeOrders = onSnapshot(
      query(collection(db, 'orders'),
        where('restaurantId', '==', restaurantId),
        where('status', 'in', ['قيد التنفيذ 🚀', 'جاري التوصيل 🛵', 'completed'])
      ),
      (snapshot) => {
        snapshot.docChanges().forEach(change => {
          if (change.type === 'modified') {
            setCurrentOrder({ id: change.doc.id, ...change.doc.data() });
          }
        });
      });

    getDoc(doc(db, 'restaurants', restaurantId)).then(res => {
      if (!res.exists()) return navigate('/restaurant/login');
      setRestaurantData({ id: res.id, ...res.data() });
    });

    return () => {
      unsubscribeDrivers();
      unsubscribeOrders();
    };
  }, [navigate, restaurantId]);

  const getDriverIcon = (driver) => {
    if (!window.google) return undefined;
    if (!driver.isMoving) return { url: carRed, scaledSize: new window.google.maps.Size(40, 40) };
    if (['في الطريق', 'قيد التنفيذ 🚀', 'جاري التوصيل 🛵'].includes(driver.orderStatus)) {
      return { url: carBlue, scaledSize: new window.google.maps.Size(40, 40) };
    }
    return { url: carGreen, scaledSize: new window.google.maps.Size(40, 40) };
  };

  const calculateDeliveryFee = (distanceKm) => {
    const baseFee = 1.350;
    const extraRate = 0.100;
    const threshold = 20;
    let fee = baseFee;
    if (distanceKm > threshold) fee += (distanceKm - threshold) * extraRate;
    setDeliveryFee(parseFloat(fee.toFixed(3)));
    console.log("🚚 رسوم التوصيل:", fee.toFixed(3), 'د.ك');
  };

  // تعديل الدالة handleAddressSelect
// تصحيح الدالة handleAddressSelect
const handleAddressSelect = () => {
  if (!searchBox || !restaurantData?.location || !window.google) {
    console.log("❌ أحد العناصر غير متوفر:", { searchBox, restaurantLocation: restaurantData?.location });
    return;
  }
  
  const places = searchBox.getPlaces();
  if (!places || places.length === 0) {
    console.log("❌ لم يتم تحديد أماكن");
    return;
  }

  const place = places[0];
  if (!place.geometry || !place.geometry.location) {
    console.log("❌ تم تحديد المكان لكن بدون إحداثيات:", place);
    setError('لم نتمكن من العثور على إحداثيات العنوان المحدد.');
    return;
  }

  const location = {
    lat: place.geometry.location.lat(),
    lng: place.geometry.location.lng()
  };

  console.log("✅ تم تحديد الموقع بنجاح:", location);

  setDeliveryInfo(prev => ({
    ...prev,
    address: place.formatted_address || `${place.name} ${place.vicinity || ''}`,
    customerLocation: location
  }));

  // حساب المسافة والاتجاهات (هذا الجزء يجب أن يكون داخل الدالة)
  const directionsService = new window.google.maps.DirectionsService();
  directionsService.route({
    origin: new window.google.maps.LatLng(restaurantData.location.lat, restaurantData.location.lng),
    destination: new window.google.maps.LatLng(location.lat, location.lng),
    travelMode: window.google.maps.TravelMode.DRIVING
  }, (result, status) => {
    if (status === window.google.maps.DirectionsStatus.OK) {
      setDirections(result);
      const distanceInMeters = result.routes[0].legs[0].distance.value;
      const distanceInKm = distanceInMeters / 1000;
      setDistance(distanceInKm);
      calculateDeliveryFee(distanceInKm);
    } else {
      setError('لم نتمكن من حساب المسافة: ' + status);
    }
  });
};
  

  const handleShowDriverTracker = () => {
    setShowDriverTracker(true);
  };

  const handleHideDriverTracker = () => {
    setShowDriverTracker(false);
  };

  // إضافة دالة لتحديث موقع المطعم
  const updateRestaurantLocationBeforeOrder = async () => {
    // التحقق من وجود إحداثيات المطعم
    if (!restaurantData?.location || !restaurantData.location.lat || !restaurantData.location.lng) {
      console.error("❌ لا يمكن تحديث موقع المطعم - البيانات غير متوفرة");
      return false;
    }
    
    try {
      // استخدام المكان الحالي كموقع محدث للمطعم
      if (navigator.geolocation) {
        return new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const currentLocation = {
                lat: position.coords.latitude,
                lng: position.coords.longitude
              };
              
              try {
                // استدعاء الدالة السحابية لتحديث الموقع
                const updateRestaurantLocation = httpsCallable(functions, 'updateRestaurantLocation');
                await updateRestaurantLocation({ 
                  location: currentLocation,
                  updateSource: 'new_order'
                });
                
                console.log("✅ تم تحديث موقع المطعم قبل إرسال الطلب:", currentLocation);
                
                // تحديث البيانات المحلية
                setRestaurantData(prev => ({
                  ...prev,
                  location: currentLocation
                }));
                
                setRestaurantLocation(currentLocation);
                resolve(true);
              } catch (error) {
                console.error("❌ خطأ في تحديث موقع المطعم:", error);
                // الاستمرار بالموقع الحالي المخزن بالفعل
                resolve(true);
              }
            },
            (error) => {
              console.error("❌ خطأ في تحديد الموقع الحالي:", error);
              // الاستمرار بالموقع الحالي المخزن بالفعل
              resolve(true);
            },
            { enableHighAccuracy: true, timeout: 5000, maximumAge: 0 }
          );
        });
      }
      
      return true; // إذا لم تكن geolocation متاحة، الاستمرار بالموقع الحالي
    } catch (error) {
      console.error("❌ خطأ غير متوقع أثناء تحديث الموقع:", error);
      return true; // الاستمرار بالموقع الحالي في حالة الخطأ
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!restaurantId || !selectedDriver) return setError('يجب اختيار سائق.');
  
    // تحقق مبكر من وجود بيانات المطعم والموقع
    if (!restaurantData) {
      console.error("❌ بيانات المطعم غير متوفرة");
      setError('لم يتم تحميل بيانات المطعم بشكل صحيح. يرجى تحديث الصفحة.');
      setSubmitting(false);
      return;
    }
  
    if (!restaurantData.location || !restaurantData.location.lat || !restaurantData.location.lng) {
      console.error("❌ موقع المطعم غير محدد:", restaurantData);
      setError('لم يتم تحديد موقع المطعم بشكل صحيح. يرجى التواصل مع المسؤول.');
      setSubmitting(false);
      return;
    }

    const requiredFields = ['customerName', 'phone', 'address', 'amount'];
    const missingFields = requiredFields.filter(field => {
      const value = deliveryInfo[field];
      if (typeof value === 'string') return !value.trim();
      return value === null || value === undefined || value === '';
    });

    if (missingFields.length > 0) {
      return setError(`الحقول التالية مطلوبة: ${missingFields.join(', ')}`);
    }

    const amount = parseFloat(deliveryInfo.amount);
    if (isNaN(amount) || amount <= 0) return setError('قيمة الطلب غير صحيحة.');
    if (!restaurantData || restaurantData.balance < deliveryFee) return setError('الرصيد لا يكفي.');

    setSubmitting(true);
    
    // تحديث موقع المطعم قبل إرسال الطلب
    await updateRestaurantLocationBeforeOrder();
    
    // استخدام الموقع المحدث للمطعم (إذا كان متاحًا) أو الاعتماد على الموقع المخزن
    const actualRestaurantLocation = restaurantLocation || restaurantData.location;

    let customerLocation = deliveryInfo.customerLocation;

    // جلب من directions إذا لم تكن موجودة
    if (!isValidCoordinate(customerLocation)) {
      if (directions?.routes[0]?.legs[0]?.end_location) {
        customerLocation = {
          lat: directions.routes[0].legs[0].end_location.lat(),
          lng: directions.routes[0].legs[0].end_location.lng()
        };
      }
    }

    // تحقق النهائي بعد كل المحاولات
    if (!isValidCoordinate(customerLocation)) {
      console.error("❌ موقع العميل غير صالح:", customerLocation);
      setError('لم يتم تحديد موقع العميل بشكل دقيق. يرجى تحديد العنوان على الخريطة.');
      setSubmitting(false);
      return;
    }

    // إعادة حساب المسافة ورسوم التوصيل باستخدام الموقع المحدث للمطعم
    if (window.google && actualRestaurantLocation && customerLocation) {
      const directionsService = new window.google.maps.DirectionsService();
      try {
        const result = await new Promise((resolve, reject) => {
          directionsService.route({
            origin: new window.google.maps.LatLng(actualRestaurantLocation.lat, actualRestaurantLocation.lng),
            destination: new window.google.maps.LatLng(customerLocation.lat, customerLocation.lng),
            travelMode: window.google.maps.TravelMode.DRIVING
          }, (response, status) => {
            if (status === window.google.maps.DirectionsStatus.OK) {
              resolve(response);
            } else {
              reject(status);
            }
          });
        });
        
        setDirections(result);
        const distanceInMeters = result.routes[0].legs[0].distance.value;
        const distanceInKm = distanceInMeters / 1000;
        setDistance(distanceInKm);
        calculateDeliveryFee(distanceInKm);
        
        console.log("✅ تم إعادة حساب المسافة باستخدام الموقع المحدث:", distanceInKm.toFixed(2), "كم");
      } catch (error) {
        console.error("❌ خطأ في إعادة حساب المسافة:", error);
        // الاستمرار بالمسافة السابقة
      }
    }

    let newOrderCode = '';
    try {
      const counterRef = doc(db, 'counters', 'orders');
      await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef);
        const lastId = counterDoc.data().lastId || 0;
        const nextId = lastId + 1;
        const paddedId = String(nextId).padStart(6, '0');
        newOrderCode = `ATD${paddedId}`;
        transaction.update(counterRef, { lastId: nextId });
      });
    } catch (err) {
      setError('فشل توليد رقم الطلب.');
      setSubmitting(false);
      return;
    }

    const orderData = {
      orderCode: newOrderCode,
      restaurantId,
      restaurantName: restaurantData.name,
      restaurantAddress: restaurantData.address,
      restaurantLocation: new GeoPoint(actualRestaurantLocation.lat, actualRestaurantLocation.lng),
      driverId: selectedDriver.id,
      driverName: selectedDriver.name,
      customerName: deliveryInfo.customerName.trim(),
      phone: deliveryInfo.phone.trim(),
      address: deliveryInfo.address.trim(),
      notes: deliveryInfo.notes?.trim() || '',
      amount,
      deliveryFee,
      totalAmount: parseFloat((amount + deliveryFee).toFixed(3)),
      distance,
      paymentMethod: deliveryInfo.paymentMethod,
      status: 'قيد التنفيذ 🚀',
      createdAt: serverTimestamp(),
      customerLocation: new GeoPoint(customerLocation.lat, customerLocation.lng),
      notificationSent: false
    };

    try {
      const orderRef = await addDoc(collection(db, 'orders'), orderData);
      await updateDoc(doc(db, 'restaurants', restaurantId), {
        balance: restaurantData.balance - deliveryFee
      });
      await addDoc(collection(db, 'transactions'), {
        restaurantId,
        type: 'خصم',
        amount: deliveryFee,
        description: `رسوم توصيل للطلب #${orderRef.id}`,
        timestamp: serverTimestamp(),
        orderId: orderRef.id
      });

      // في NewOrder.js - تعديل جزء إرسال الإشعار
      await fetch("https://sendordernotification-f6l6kr6lva-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fcmToken: selectedDriver.fcmToken,
          title: "طلب جديد",
          body: `طلب جديد من ${restaurantData.name} - ${orderRef.id}`,
          orderId: orderRef.id,
          restaurantLocation: {
            lat: Number(restaurantData.location.lat),
            lng: Number(restaurantData.location.lng)
          },
          // إضافة رابط مباشر للخريطة
          directionsUrl: `https://www.google.com/maps/dir/?api=1&destination=${restaurantData.location.lat},${restaurantData.location.lng}&travelmode=driving`,
          customerLocation: {
            lat: Number(customerLocation.lat),
            lng: Number(customerLocation.lng)
          },
          navigationType: "COORDINATES"
        })
      });

      setSuccess(`تم إرسال الطلب بنجاح! رقم الطلب: ${newOrderCode}`);
      setDeliveryInfo({ customerName: '', phone: '', address: '', notes: '', amount: '', paymentMethod: 'نقدي', customerLocation: null });
      setSelectedDriver(null);
      setDirections(null);
      setDistance(0);
      setDeliveryFee(0);

      setTimeout(() => navigate(`/restaurant/orders/${orderRef.id}`), 3000);
    } catch (error) {
      console.error(error);
      setError('حدث خطأ أثناء إرسال الطلب.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="restaurant-new-order-page">
      <RestaurantSidebar />
      <div className="restaurant-new-order-content">
        <RestaurantHeader restaurantName={restaurantData?.name || 'المطعم'} />
        <div className="new-order-container">
          <h1 className="page-title">إرسال طلب جديد</h1>
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          {currentOrder && (
            <div className="current-order-status">
              <h3>حالة الطلب الحالية: {currentOrder.status}</h3>
              <p>رقم الطلب: {currentOrder.id}</p>
              <button onClick={handleShowDriverTracker} className="track-driver-btn">
                تتبع السائق
              </button>
            </div>
          )}

          {currentOrder && showDriverTracker && (
            <DriverTracker
              orderId={currentOrder.id}
              restaurantLocation={restaurantData?.location}
              customerLocation={currentOrder.customerLocation}
              onClose={handleHideDriverTracker}
            />
          )}

          <div className="new-order-grid">
            <div className="new-order-form-container">
              <form onSubmit={handleSubmit} className="new-order-form">
                <div className="form-section">
                  <h2>بيانات العميل</h2>

                  <div className="form-group">
                    <label htmlFor="customerName">اسم العميل *</label>
                    <input
                      type="text"
                      id="customerName"
                      value={deliveryInfo.customerName}
                      onChange={(e) => setDeliveryInfo({...deliveryInfo, customerName: e.target.value})}
                      disabled={submitting}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="phone">رقم الهاتف *</label>
                    <input
                      type="tel"
                      id="phone"
                      value={deliveryInfo.phone}
                      onChange={(e) => setDeliveryInfo({...deliveryInfo, phone: e.target.value})}
                      disabled={submitting}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="address">العنوان *</label>
                    <StandaloneSearchBox 
  onLoad={ref => {
    console.log("✅ تم تحميل مربع البحث");
    setSearchBox(ref);
  }}
  onPlacesChanged={handleAddressSelect}
>
  <input
    type="text"
    value={deliveryInfo.address}
    onChange={(e) => setDeliveryInfo({ ...deliveryInfo, address: e.target.value })}
    placeholder="اختر العنوان أو انقر على الخريطة..."
    id="autocomplete-input"
    style={{
      boxSizing: 'border-box',
      border: '1px solid #ddd',
      width: '100%',
      height: '40px',
      padding: '0 12px',
      borderRadius: '4px',
      fontSize: '14px',
      outline: 'none',
    }}
  />
</StandaloneSearchBox>
                  </div>

                  <div className="form-group">
                    <label htmlFor="notes">ملاحظات</label>
                    <textarea
                      id="notes"
                      value={deliveryInfo.notes}
                      onChange={(e) => setDeliveryInfo({...deliveryInfo, notes: e.target.value})}
                      disabled={submitting}
                    />
                  </div>
                </div>

                <div className="form-section">
                  <h2>تفاصيل الطلب</h2>

                  <div className="form-group">
                    <label htmlFor="amount">قيمة الطلب (د.ك) *</label>
                    <input
                      type="number"
                      id="amount"
                      value={deliveryInfo.amount}
                      onChange={(e) => setDeliveryInfo({...deliveryInfo, amount: e.target.value})}
                      disabled={submitting}
                      step="0.001"
                      min="0"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="paymentMethod">طريقة الدفع *</label>
                    <select
                      id="paymentMethod"
                      value={deliveryInfo.paymentMethod}
                      onChange={(e) => setDeliveryInfo({...deliveryInfo, paymentMethod: e.target.value})}
                      disabled={submitting}
                      required
                    >
                      <option value="نقدي">نقدي</option>
                      <option value="كي نت">كي نت</option>
                      <option value="بطاقة ائتمان">بطاقة ائتمان</option>
                    </select>
                  </div>

                  {distance > 0 && (
  <div className="delivery-info">
    <div className="info-item">
      <span className="info-label">المسافة:</span>
      <span className="info-value">{distance.toFixed(2)} كم</span>
    </div>
    <div className="info-item">
      <span className="info-label">رسوم التوصيل:</span>
      <span className="info-value">{deliveryFee.toFixed(3)} د.ك</span>
    </div>
  </div>
)}
{loading && (
  <div className="loading-distance">
    جاري حساب المسافة...
  </div>
)}

                </div>

                <div className="form-section">
                  <h2>اختر السائق</h2>
                  {drivers.length === 0 ? (
                    <p className="no-drivers">لا يوجد سائقين متاحين حالياً</p>
                  ) : (
                    <div className="drivers-list">
                      {drivers.map(driver => (
                        <div 
                          key={driver.id} 
                          className={`driver-card ${selectedDriver?.id === driver.id ? 'selected' : ''}`}
                          onClick={() => setSelectedDriver(driver)}
                        >
                          <div className="driver-avatar">{driver.name.charAt(0)}</div>
                          <div className="driver-info">
                            <h3>{driver.name}</h3>
                            <p>{driver.phone}</p>
                          </div>
                          <div className="driver-status">متاح</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="form-actions">
                  <button 
                    type="submit" 
                    className="submit-btn" 
                    disabled={submitting || drivers.length === 0}
                  >
                    {submitting ? 'جاري الإرسال...' : 'إرسال الطلب'}
                  </button>
                </div>
              </form>
            </div>

            <div className="new-order-form-container" style={{ marginBottom: '15px' }}>
              <div className="form-section">
                <h2>تعليمات استخدام الخريطة</h2>
                <ul style={{ listStyleType: 'disc', paddingRight: '20px' }}>
                  <li>يمكنك النقر على أي مكان في الخريطة لتحديد موقع العميل بدقة</li>
                  <li>يمكنك إدخال الإحداثيات مباشرة في حقل العنوان بالصيغة: 29.3759, 47.9774</li>
                  <li>تأكد من ظهور علامة "العميل" على الخريطة قبل إرسال الطلب</li>
                </ul>
              </div>
            </div>

            <div className="map-container">
              {!isLoaded && !loadError && (
                <div className="map-loading">جاري تحميل الخريطة...</div>
              )}
              {loadError && (
                <div className="map-error">
                  حدث خطأ في تحميل الخريطة. يرجى المحاولة مرة أخرى.
                </div>
              )}
              {isLoaded && !loadError && (
               <GoogleMap
               mapContainerStyle={{ width: '100%', height: '100%' }}
               center={
                 restaurantData?.location
                   ? { lat: restaurantData.location.lat, lng: restaurantData.location.lng }
                   : { lat: 29.3759, lng: 47.9774 }
               }
               zoom={12}
               options={{
                 streetViewControl: false,
                 fullscreenControl: false,
                 gestureHandling: 'greedy',
                 zoomControl: true,
                 draggable: true,
                 scrollwheel: true
               }}
               onClick={(e) => {
                 if (e.latLng) {
                   const location = {
                     lat: e.latLng.lat(),
                     lng: e.latLng.lng()
                   };
                   
                   console.log("✅ تم تحديد الموقع بالنقر على الخريطة:", location);
                   
                   setDeliveryInfo(prev => ({
                     ...prev,
                     customerLocation: location,
                     address: `${location.lat.toFixed(6)}, ${location.lng.toFixed(6)}`
                   }));
                   
                   // حساب المسافة والاتجاهات
                   if (restaurantData?.location) {
                     const directionsService = new window.google.maps.DirectionsService();
                     directionsService.route({
                       origin: new window.google.maps.LatLng(restaurantData.location.lat, restaurantData.location.lng),
                       destination: new window.google.maps.LatLng(location.lat, location.lng),
                       travelMode: window.google.maps.TravelMode.DRIVING
                     }, (result, status) => {
                       if (status === window.google.maps.DirectionsStatus.OK) {
                         setDirections(result);
                         const distanceInMeters = result.routes[0].legs[0].distance.value;
                         const distanceInKm = distanceInMeters / 1000;
                         setDistance(distanceInKm);
                         calculateDeliveryFee(distanceInKm);
                       }
                     });
                   }
                 }
               }}
             >
               {renderDriverMarkers()}
               
               {deliveryInfo.customerLocation && 
                 isValidCoordinate(deliveryInfo.customerLocation) && (
                   <Marker
                     position={{
                       lat: Number(deliveryInfo.customerLocation.lat),
                       lng: Number(deliveryInfo.customerLocation.lng)
                     }}
                     icon={{
                       url: '/assets/customer-marker.png',
                       scaledSize: new window.google.maps.Size(40, 40)
                     }}
                     label={{
                       text: 'العميل',
                       color: '#ffffff',
                       fontSize: '12px',
                       fontWeight: 'bold'
                     }}
                   />
                 )}
             
               {directions && (
                 <DirectionsRenderer
                   directions={directions}
                   options={{
                     suppressMarkers: true,
                     polylineOptions: {
                       strokeColor: '#4a90e2',
                       strokeWeight: 5
                     }
                   }}
                 />
               )}
             </GoogleMap>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


export default NewOrder;